export type LandingLocale = "en" | "de"

export type LandingCopy = {
  metaTitle: string
  metaDescription: string
  impressumMetaTitle: string
  navAbout: string
  navBrands: string
  navPartners: string
  navCareers: string
  navContact: string
  teamLogin: string
  heroTitle: string
  heroSubtitle: string
  ctaAmazon: string
  ctaContact: string
  aboutTitle: string
  aboutSubtitle: string
  aboutCard1Title: string
  aboutCard1Body: string
  aboutCard2Title: string
  aboutCard2Body: string
  brandsTitle: string
  brandsSubtitle: string
  partnerBrandsTitle: string
  partnerBrandsBody: string
  tagPartner: string
  partnersTitle: string
  partnersSubtitle: string
  partnersCard1Title: string
  partnersCard1Body: string
  partnersCard2Title: string
  partnersCard2Body: string
  partnersCard3Title: string
  partnersCard3Body: string
  partnersCard4Title: string
  partnersCard4Body: string
  ctaPartners: string
  careersTitle: string
  careersSubtitle: string
  careersCard1Title: string
  careersCard1Body: string
  careersCard2Title: string
  careersCard2Body: string
  ctaCareers: string
  colozooBody: string
  omicubeBody: string
  lobomeBody: string
  visitBrand: string
  contactTitle: string
  contactFormTitle: string
  contactSubtitle: string
  contactSellerProfile: string
  contactNameLabel: string
  contactEmailLabel: string
  contactMessageLabel: string
  contactSubmit: string
  contactSending: string
  contactSuccess: string
  contactError: string
  impressumTitle: string
  impressumBack: string
  impressumDirectors: string
  impressumCourt: string
  impressumRegister: string
  impressumVat: string
  impressumContactTitle: string
  impressumContactNote: string
  impressumGeneral: string
  footerTagline: string
  copyright: string
}

const COPY: Record<LandingLocale, LandingCopy> = {
  en: {
    metaTitle: "Viridian GmbH | E-Commerce & Brand Management",
    metaDescription:
      "Viridian GmbH develops and distributes consumer brands across European Amazon marketplaces.",
    impressumMetaTitle: "Impressum | Viridian GmbH",
    navAbout: "About",
    navBrands: "Brands",
    navPartners: "Partners",
    navCareers: "Careers",
    navContact: "Contact",
    teamLogin: "Team login",
    heroTitle: "Building & distributing consumer brands across Europe",
    heroSubtitle:
      "Viridian GmbH is a German e-commerce company focused on brand development, Amazon distribution, and pan-European retail operations.",
    ctaAmazon: "Our Amazon Store",
    ctaContact: "Contact",
    aboutTitle: "About Viridian GmbH",
    aboutSubtitle:
      "A decade of Amazon expertise, in-house operations, and distribution across major European marketplaces.",
    aboutCard1Title: "Designed & managed in Germany",
    aboutCard1Body:
      "Headquartered in Halle (Saale), we develop and manage proprietary consumer brands with strict quality and compliance standards.",
    aboutCard2Title: "Pan-European distribution",
    aboutCard2Body:
      "We sell across Amazon marketplaces in the EU and UK, with localized logistics and full VAT registration across Europe.",
    brandsTitle: "Brands & portfolio",
    brandsSubtitle:
      "Our own consumer brands and official distribution partnerships with leading manufacturers.",
    partnerBrandsTitle: "Distribution partners",
    partnerBrandsBody:
      "Official distributor and online retailer for Nevskaya Palitra, Sonnet, Decola, White Nights, and related product lines.",
    tagPartner: "Official partner",
    partnersTitle: "For brand partners & suppliers",
    partnersSubtitle:
      "Scale on European marketplaces without operational friction — we buy stock and manage the full customer-facing lifecycle.",
    partnersCard1Title: "10+ years on Amazon",
    partnersCard1Body:
      "Deep expertise in Amazon PPC, SEO, listings, and conversion optimization across EU and UK marketplaces.",
    partnersCard2Title: "Proprietary tech stack",
    partnersCard2Body:
      "Custom internal tools for inventory forecasting, demand planning, and automated channel management.",
    partnersCard3Title: "Full EU compliance",
    partnersCard3Body:
      "Pan-European VAT, packaging rules, customs, and platform policies handled in-house.",
    partnersCard4Title: "In-house execution",
    partnersCard4Body:
      "No outsourced agencies — our team handles creative, store management, inventory, and logistics directly.",
    ctaPartners: "Partner with us",
    careersTitle: "Work with us",
    careersSubtitle:
      "A remote-first team built around autonomy, operational excellence, and proprietary technology.",
    careersCard1Title: "Global & remote-first",
    careersCard1Body:
      "We collaborate across time zones with clear communication, self-direction, and a focus on results.",
    careersCard2Title: "Careers at Viridian",
    careersCard2Body:
      "We welcome talent in e-commerce, logistics, design, and software — even when no role is open today.",
    ctaCareers: "Get in touch",
    colozooBody:
      "Art, craft, and creative supplies designed for vibrant performance and everyday creative use.",
    omicubeBody:
      "Consumer products developed under German design and quality standards.",
    lobomeBody:
      "Practical consumer product lines for modern households and daily use.",
    visitBrand: "Visit website",
    contactTitle: "Contact",
    contactFormTitle: "Send a message",
    contactSubtitle: "We reply by email.",
    contactSellerProfile: "Amazon seller profile",
    contactNameLabel: "Your name",
    contactEmailLabel: "Your email",
    contactMessageLabel: "Message",
    contactSubmit: "Send message",
    contactSending: "Sending…",
    contactSuccess: "Thank you — your message was sent.",
    contactError: "Could not send your message. Please try again later.",
    impressumTitle: "Impressum",
    impressumBack: "Back to homepage",
    impressumDirectors: "Managing directors (Geschäftsführer)",
    impressumCourt: "Registration court",
    impressumRegister: "Commercial register no. (Handelsregisternummer)",
    impressumVat: "VAT ID (USt-IdNr.)",
    impressumContactTitle: "Contact",
    impressumContactNote:
      "Prefer email? Use the form below or write to us directly.",
    impressumGeneral: "Email",
    footerTagline: "E-commerce, brand management & distribution across Europe.",
    copyright: "All rights reserved.",
  },
  de: {
    metaTitle: "Viridian GmbH | E-Commerce & Markenmanagement",
    metaDescription:
      "Viridian GmbH entwickelt und vertreibt Konsumgütermarken auf europäischen Amazon-Marktplätzen.",
    impressumMetaTitle: "Impressum | Viridian GmbH",
    navAbout: "Unternehmen",
    navBrands: "Marken",
    navPartners: "Partner",
    navCareers: "Karriere",
    navContact: "Kontakt",
    teamLogin: "Team-Login",
    heroTitle: "Konsumgütermarken in Europa aufbauen und vertreiben",
    heroSubtitle:
      "Viridian GmbH ist ein in Deutschland ansässiges E-Commerce-Unternehmen für Markenentwicklung, Amazon-Vertrieb und paneuropäischen Handel.",
    ctaAmazon: "Unser Amazon-Shop",
    ctaContact: "Kontakt",
    aboutTitle: "Über Viridian GmbH",
    aboutSubtitle:
      "Über ein Jahrzehnt Amazon-Expertise, eigene Abläufe und Vertrieb auf den wichtigsten europäischen Marktplätzen.",
    aboutCard1Title: "Entwickelt & geführt in Deutschland",
    aboutCard1Body:
      "Mit Sitz in Halle (Saale) entwickeln und führen wir eigene Konsumgütermarken mit hohen Qualitäts- und Compliance-Anforderungen.",
    aboutCard2Title: "Paneuropäischer Vertrieb",
    aboutCard2Body:
      "Wir verkaufen auf Amazon-Marktplätzen in der EU und im UK mit lokalisierter Logistik und EU-weiter Umsatzsteuer-Registrierung.",
    brandsTitle: "Marken & Portfolio",
    brandsSubtitle:
      "Eigene Marken und offizielle Vertriebspartnerschaften mit etablierten Herstellern.",
    partnerBrandsTitle: "Vertriebspartner",
    partnerBrandsBody:
      "Offizieller Distributor und Online-Händler für Nevskaya Palitra, Sonnet, Decola, White Nights und verwandte Produktlinien.",
    tagPartner: "Offizieller Partner",
    partnersTitle: "Für Markenpartner & Lieferanten",
    partnersSubtitle:
      "Skalieren Sie auf europäischen Marktplätzen ohne operative Reibung — wir kaufen Ware ein und steuern den gesamten Kundenkontakt.",
    partnersCard1Title: "10+ Jahre Amazon",
    partnersCard1Body:
      "Tiefe Expertise in Amazon-PPC, SEO, Listings und Conversion-Optimierung in der EU und im UK.",
    partnersCard2Title: "Eigene Technologie",
    partnersCard2Body:
      "Interne Tools für Bestandsprognosen, Bedarfsplanung und automatisiertes Channel-Management.",
    partnersCard3Title: "EU-Compliance",
    partnersCard3Body:
      "Paneuropäische Umsatzsteuer, Verpackungsvorschriften, Zoll und Plattformrichtlinien — alles intern.",
    partnersCard4Title: "Alles in-house",
    partnersCard4Body:
      "Keine Agenturen — unser Team übernimmt Kreativ, Shop-Management, Bestand und Logistik direkt.",
    ctaPartners: "Partner werden",
    careersTitle: "Arbeiten bei uns",
    careersSubtitle:
      "Ein remote-first Team mit Fokus auf Eigenverantwortung, operative Exzellenz und eigener Technologie.",
    careersCard1Title: "Global & remote-first",
    careersCard1Body:
      "Wir arbeiten über Zeitzonen hinweg mit klarer Kommunikation, Eigeninitiative und Ergebnisfokus.",
    careersCard2Title: "Karriere bei Viridian",
    careersCard2Body:
      "Talente aus E-Commerce, Logistik, Design und Software sind willkommen — auch ohne offene Stelle.",
    ctaCareers: "Kontakt aufnehmen",
    colozooBody:
      "Kunst-, Bastel- und Kreativprodukte für lebendige Farben und kreativen Alltag.",
    omicubeBody:
      "Konsumgüter nach deutschen Design- und Qualitätsstandards.",
    lobomeBody:
      "Praktische Konsumgüter für moderne Haushalte und den täglichen Bedarf.",
    visitBrand: "Zur Website",
    contactTitle: "Kontakt",
    contactFormTitle: "Nachricht senden",
    contactSubtitle: "Wir antworten per E-Mail.",
    contactSellerProfile: "Amazon-Verkäuferprofil",
    contactNameLabel: "Ihr Name",
    contactEmailLabel: "Ihre E-Mail",
    contactMessageLabel: "Nachricht",
    contactSubmit: "Nachricht senden",
    contactSending: "Wird gesendet…",
    contactSuccess: "Vielen Dank — Ihre Nachricht wurde gesendet.",
    contactError: "Nachricht konnte nicht gesendet werden. Bitte später erneut versuchen.",
    impressumTitle: "Impressum",
    impressumBack: "Zur Startseite",
    impressumDirectors: "Geschäftsführer",
    impressumCourt: "Registergericht",
    impressumRegister: "Handelsregisternummer",
    impressumVat: "USt-IdNr.",
    impressumContactTitle: "Kontakt",
    impressumContactNote:
      "Am liebsten per E-Mail — Formular unten oder direkte Adresse.",
    impressumGeneral: "E-Mail",
    footerTagline: "E-Commerce, Markenmanagement und Vertrieb in Europa.",
    copyright: "Alle Rechte vorbehalten.",
  },
}

export function landingCopy(locale: LandingLocale): LandingCopy {
  return COPY[locale]
}

export const AMAZON_STOREFRONT_URL =
  "https://www.amazon.de/s?me=AQVAZFP6U38FK&marketplaceID=A1PA6795UKMFR9"

export const AMAZON_SELLER_URL =
  "https://www.amazon.de/sp?ie=UTF8&seller=AQVAZFP6U38FK"

export const COLOZOO_URL = "https://colozoo.de/"

export const TEAM_APP_URL = process.env.REACT_APP_APP_URL || "https://app.viridian.gmbh"

export type OwnedBrand = {
  name: string
  logo: string
  logoClass: string
  bodyKey: "colozooBody" | "omicubeBody" | "lobomeBody"
  href?: string
}

export const OWNED_BRANDS: OwnedBrand[] = [
  { name: "Colozoo", logo: "/landing/brands/colozoo.svg", logoClass: "is-colozoo", bodyKey: "colozooBody", href: COLOZOO_URL },
  { name: "Omicube", logo: "/landing/brands/omicube.jpg", logoClass: "is-omicube", bodyKey: "omicubeBody" },
  { name: "Lobome", logo: "/landing/brands/lobome.jpg", logoClass: "is-lobome", bodyKey: "lobomeBody" },
]

// Obfuscated for Impressum reveal only (not plain text in page source).
export const IMPRESSUM_CONTACT = {
  email: atob("aW5mb0B2aXJpZGlhbi5nbWJo"),
}
